import React, { useState, useEffect } from 'react'
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifFormField,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifModalFooter,
  MotifButton,
  MotifLabel,
  MotifSelect,
  MotifOption,
  MotifInput,
  MotifErrorMessage,
} from '@ey-xd/motif-react'
import { IconoirUpload } from '@ey-xd/motif-icon'
import { useParams } from 'react-router-dom'
import { formatBytes } from '@/utils/common'
import { fileExtensions } from '@/services/documents/documentsReducer'
import './Upload.scss'
import {
  useUploadEvidenceMutation,
  useCancelEvidenceMutation,
  useConfirmEvidenceMutation,
  useGetEnvironmentQuery,
} from '@/services/application/evidenceReducer'

type Props = {
  isVisible: boolean
  setIsVisible: React.Dispatch<React.SetStateAction<boolean>>
  setUploadDocId: React.Dispatch<React.SetStateAction<string>>
  evidenceType?: number
}
type FileStatus = {
  name: string
  documentID: string
  isError: boolean
  errorText: string
}

export default function UploadEvidence({
  isVisible,
  setIsVisible,
  setUploadDocId,
  evidenceType,
}: Props) {
  const [uploadedFile, setUploadedFile] = useState<File[] | null>(null)
  const [uploadedFileStatus, setUploadedFileStatus] = useState<FileStatus[]>([])
  const [isError, setIsError] = useState<boolean | false>(false)
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('')
  const [otherEnvironment, setOtherEnvironment] = useState<string>('')
  const { applicationId } = useParams<{ applicationId: string }>()
  const [uploadEvidence] = useUploadEvidenceMutation()
  const [cancelEvidence] = useCancelEvidenceMutation()
  const [confirmDocument, { isLoading }] = useConfirmEvidenceMutation()
  const { data: environmentList } = useGetEnvironmentQuery()


  useEffect(() => {
    const uploadFile = async () => {
      if (uploadedFile) {
        for (const file of uploadedFile) {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('Name', file.name.replace(/\.[^/.]+$/, ''))
          formData.append('FileName', file.name.replace(/\.[^/.]+$/, ''))
          try {
            const response = await uploadEvidence({
              applicationId,
              evidenceType: evidenceType || 1,
              formData,
            }).unwrap()
            console.log(file.name, uploadedFileStatus)

            setUploadedFileStatus((prevState) =>
              prevState.map((fileStatus) => {
                if (fileStatus.name === file.name) {
                  return {
                    ...fileStatus,
                    documentID: response,
                    isError: false,
                    errorText: '',
                  }
                }
                return fileStatus
              }),
            )
          } catch (error: any) {
            console.log(`Error uploading file ${file.name}:`, error)
            setUploadedFileStatus((prevState) =>
              prevState.map((fileStatus) => {
                if (fileStatus.name === file.name) {
                  return {
                    ...fileStatus,
                    documentID: '',
                    isError: true,
                    errorText: error?.data?.message || 'Upload failed',
                  }
                }
                return fileStatus
              }),
            )
          }
        }
      }
    }
    uploadFile()
  }, [uploadedFile])

  const handleFileChange = async (files: any) => {
    if (files.length === 0) {
      return
    }
    if (
      files[0] &&
      Object.keys(fileExtensions.custom).includes(files[0].type)
    ) {
      let fileStatus = files.map((file: any) => ({
        name: file.name,
        documentID: '',
        isError: false,
        errorText: '',
      }))
      setUploadedFileStatus((prevState) => [...prevState, ...fileStatus])
      setUploadedFile((prevState) => [...(prevState || []), ...files])
      setIsError(false)
    } else {
      setIsError(true)
    }
  }

  const handleFileUploadCancel = async () => {
    setIsError(false)
    const evidenceIds = uploadedFileStatus.filter(f => f.documentID !== '').map((file) => file.documentID)
    if (evidenceIds.length > 0) {
      const response = await cancelEvidence(evidenceIds).unwrap()
      if (response) {
        setUploadedFileStatus([])
        setIsVisible(false)
        setUploadedFile(null)
        setOtherEnvironment('')
        setSelectedEnvironment('')
        setUploadDocId('')
      }
    } else {
      setUploadedFileStatus([])
      setIsVisible(false)
      setUploadedFile(null)
      setOtherEnvironment('')
      setSelectedEnvironment('')
      setUploadDocId('')
    }
  }

  const handleConfirmUpload = async () => {
    const evidenceIds = uploadedFileStatus.map((file) => file.documentID)
    const selectedEnv =
      selectedEnvironment === 'Other' ? otherEnvironment : selectedEnvironment
    const evidenceData = {
      body: evidenceIds,
      environment: selectedEnv,
    }
    const response = await confirmDocument(evidenceData).unwrap()
    if (response) {
      setUploadedFileStatus([])
      setIsVisible(false)
      setUploadedFile(null)
      setOtherEnvironment('')
      setSelectedEnvironment('')
      setUploadDocId('')
    }
  }

  const returnFileList = (files: File[]) => {
    return (
      files &&
      files.length > 0 &&
      files.map((file, index) => (
        <div className="motif-col-md-4 mb10px" key={index}>
          <MotifFileUploaderItem
            key={index}
            fileIcon={<MotifIcon src={IconoirUpload} />}
            fileName={file.name}
            uploaded={uploadedFileStatus[index]?.documentID !== ''}
            uploadedText={formatBytes(file.size)}
            error={uploadedFileStatus[index]?.isError}
            errorText={uploadedFileStatus[index]?.errorText}
            isIndeterminate
            percentUploaded={20}
            showProgress={true}
            className="file-uploader-item"
          />
        </div>
      ))
    )
  }
  return (
    <>
      <MotifModal
        onClose={handleFileUploadCancel}
        show={isVisible}
        size="xl"
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: 'none',
          },
        }}
        className="upload-evidence-modal"
      >
        <MotifModalHeader className="no-border-bottom">
          <div className="motif-h6-default-regular">
            {evidenceType === 1
              ? 'Upload developer documents'
              : 'Upload implementer documents'}
          </div>
          <div className="motif-card-subheader">
            <span className="motif-body2-default-regular motif-caption-default-light">
              {
                'Please ensure the evidence uploaded contain relevant information to identify the users (i.e., User ID, Name, etc.). Select an environment relevant in the dropdown'
              }
            </span>
          </div>
        </MotifModalHeader>
        <MotifModalBody>
          <MotifFormField>
            <MotifLabel>Select Environment</MotifLabel>
            <MotifSelect
              ariaLabelledBy="select-label"
              onChange={(val) => {
                setSelectedEnvironment(val)
              }}
              placeholder="Select Environment"
              visibleOptions={3}
              className="mb10px"
            >
              {environmentList &&
                environmentList.map((option: { environmentType: string }) => (
                  <MotifOption
                    key={option.environmentType}
                    value={option.environmentType}
                  >
                    {option.environmentType}
                  </MotifOption>
                ))}
            </MotifSelect>
            {selectedEnvironment === 'Other' && (
              <div className="mb10px">
                <MotifInput
                  id="Other-Environment"
                  onChange={(event) => setOtherEnvironment(event.target.value)}
                  value={otherEnvironment}
                  clearButtonTitle="Clear environment"
                  placeholder="Enter New Environment"
                />
              </div>
            )}
            <MotifFileUploader
              id="my-id"
              label={'Click to upload'}
              labelDescription={
                'Maximum file size : 20MB , File Format : JPG,JPEG,PNG or PDF - A maximum of 100 images will be allowed at one time.'
              }
              onDrop={(files) => handleFileChange(files)}
              maxFileSize={10000}
              accept={fileExtensions.custom}
            >
              <div className="motif-row">
                {uploadedFile && returnFileList(uploadedFile)}
              </div>
            </MotifFileUploader>
            {isError && <MotifErrorMessage>Please convert your excel, CSV, Text files to PDFs to upload them here.</MotifErrorMessage>}
          </MotifFormField>
        </MotifModalBody>
        <MotifModalFooter>
          <div className="motif-row justify-content-end gap-1">
            <MotifButton
              onClick={handleFileUploadCancel}
              size="medium"
              type="button"
              variant="secondary"
            >
              Cancel
            </MotifButton>
            <MotifButton
              onClick={handleConfirmUpload}
              size="medium"
              type="button"
              disabled={
                selectedEnvironment === '' ||
                uploadedFileStatus.length === 0 ||
                uploadedFileStatus.some((file) => file.documentID === '') ||
                (selectedEnvironment === 'Other' && otherEnvironment.length === 0)
              }
              variant="primary"
              loading={isLoading}
            >
              Confirm
            </MotifButton>
          </div>
        </MotifModalFooter>
      </MotifModal>
    </>
  )
}